// src/sidebar/useSidebarItems.js
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  ShoppingOutlined,
  DollarOutlined,
  MessageOutlined,
  SettingOutlined,
  HomeOutlined,
  TagOutlined,
  GiftOutlined,
  BarChartOutlined,
  SafetyCertificateOutlined,
  SyncOutlined,
  CustomerServiceOutlined,
  CreditCardOutlined,
  LogoutOutlined,
  BellOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Badge } from 'antd';
import useUnreadChat from '../../hooks/useUnreadChat';

const useSidebarItems = () => {
  const userRole = localStorage.getItem('role');
  const { t } = useTranslation();
  const { count: unreadChat } = useUnreadChat();
  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };
  const items = [
    // Section: Tableau de Bord
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: t('dashboard'),
      link: '/dashboard',
    },

    // Section: Gestion des Utilisateurs
    {
      key: 'users-section',
      icon: <TeamOutlined />,
      label: t('User Management'),
      children: [
        {
          key: 'users',
          icon: <UserOutlined />,
          label: t('Users'),
          link: '/dashboard/users',
        },
        {
          key: 'clients',
          icon: <TeamOutlined />,
          label: t('Clients'),
          link: '/dashboard/client',
        },
        {
          key: 'verifications',
          icon: <SafetyCertificateOutlined />,
          label: t('User Verification'),
          link: '/dashboard/verifications',
        },
      ]
    },

    // Section: Catalogue et Offres
    {
      key: 'catalog-section',
      icon: <ShoppingOutlined />,
      label: t('Catalog Management'),
      children: [
        {
          key: 'type-offers',
          icon: <TagOutlined />,
          label: t('Offer Types'),
          link: '/dashboard/typeOffers',
        },
        {
          key: 'offers',
          icon: <GiftOutlined />,
          label: t('Offers'),
          link: '/dashboard/offers',
        },
        {
          key: 'currencies',
          icon: <DollarOutlined />,
          label: t('Currencies'),
          link: '/dashboard/currencies',
        },
        {
          key: 'coupons',
          icon: <TagOutlined />,
          label: t('Coupons'),
          link: '/dashboard/coupons',
        },
      ]
    },

    // Section: Transactions et Ventes
    {
      key: 'transactions-section',
      icon: <DollarOutlined />,
      label: t('Transactions'),
      children: [
        {
          key: 'sales',
          icon: <ShoppingOutlined />,
          label: t('Sales'),
          link: '/dashboard/ventes',
        },
        {
          key: 'purchases',
          icon: <ShoppingOutlined />,
          label: t('Purchases'),
          link: '/dashboard/achat',
        },
        {
          key: 'refunds',
          icon: <SyncOutlined />,
          label: t('Refund Requests'),
          link: '/dashboard/refund',
        },
      ]
    },

    // Section: Analytics et Rapports
    {
      key: 'analytics-section',
      icon: <BarChartOutlined />,
      label: t('Analytics'),
      children: [
        {
          key: 'average-amount',
          icon: <BarChartOutlined />,
          label: t('Average Amount'),
          link: '/dashboard/average',
        },
        // Vous pouvez ajouter d'autres rapports ici
        // {
        //   key: 'reports',
        //   icon: <FileTextOutlined />,
        //   label: t('Reports'),
        //   link: '/dashboard/reports',
        // },
      ]
    },

    // Section: Communication
    {
      key: 'communication-section',
      icon: <MessageOutlined />,
      label: t('Communication'),
      children: [
        {
          key: 'messages',
          icon: <MessageOutlined />,
          label: t('Messages'),
          link: '/dashboard/messages',
        },
        {
          key: 'support',
          icon: (
            <Badge dot={unreadChat > 0} offset={[2, 0]}>
              <CustomerServiceOutlined />
            </Badge>
          ),
          label: t('Support Chat'),
          link: '/dashboard/chat',
        },
      ]
    },

    // Section: Paramètres
    {
      key: 'settings-section',
      icon: <SettingOutlined />,
      label: t('Settings'),
      children: [
        {
          key: 'payment-settings',
          icon: <CreditCardOutlined />,
          label: t('Payment Settings'),
          link: '/dashboard/settingsPayment',
        },
        {
          key: 'system-settings',
          icon: <BellOutlined />,
          label: t('System Settings'),
          link: '/dashboard/settingsSystem',
        },
      ]
    },

    // Retour au site principal
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: t('Back to Website'),
      link: '/',
      style: { marginTop: '20px', borderTop: '1px solid #f0f0f0', paddingTop: '10px' }
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('Logout'),
      onClick: handleLogout,
      style: {
        marginTop: '10px',
        // borderTop: '1px solid #f0f0f0', 
        paddingTop: '10px',
        color: '#ff4d4f'
      },
      danger: true
    },
  ];

  // Filtrage basé sur le rôle utilisateur (décommentez si nécessaire)
  const filteredItems = items.filter(item => {
    if (userRole === 'admin') {
      return true; // Admin voit tout
    }
    if (userRole === 'moderator') {
      // Exemple: Les modérateurs n'ont pas accès aux paramètres de paiement
      return item.key !== 'payment-settings';
    }
    if (userRole === 'support') {
      // Exemple: Le support n'a accès qu'à la communication
      return ['communication-section', 'home'].includes(item.key);
    }
    return true; // Par défaut, tout est visible
  });

  return filteredItems;
};

export default useSidebarItems;